/**
 * Delta — la pastille « +2,5 % » / « −1,6 % » à côté d'un chiffre : vert quand
 * c'est bon, rouge quand c'est mauvais (`inverse` pour un coût, où monter est
 * mauvais). Mêmes teintes que le `DeltaChip` de `MetricCard` et le `Delta` du
 * tableau de bord RishFans, en pilule et en fr-FR.
 *
 * `"nouveau"` (27-10-2026) : la période d'avant valait ZÉRO et celle-ci non —
 * aucun pourcentage ne se dit (« +∞ % »), mais ce n'est pas un inconnu non
 * plus : la pastille le dit en un mot (« Nouveau »), dans la teinte d'une
 * montée. Un inconnu, lui, n'a pas de pastille du tout (l'écran ne passe rien).
 *
 * Vague 2 (02-11-2026), ce que le `DeltaChip` faisait et que la pastille avait
 * perdu : au-delà de +900 % le pourcentage ne se lit plus (« +1 250 % ») — la
 * pastille dit « ×13,5 » ; et le détail (« +1 250 % vs la période d'avant »)
 * est dans une `Infobulle`, au survol. `sansInfobulle` dans une cellule
 * serrée : le détail reste en `title`. Les mots viennent des libellés de la
 * librairie (`<Libelles>`), `libelleNouveau` et `libelleVs` l'emportent.
 */
import { Infobulle } from "../Infobulle";
import { useLibelles } from "../libelles";
import { cn } from "../utils";

import { formatNombre, formatPourcent } from "./format";

/** Une variation : un % signé, ou `"nouveau"` (rien avant, quelque chose maintenant). */
export type ValeurDelta = number | "nouveau";

/** Au-delà de ce pourcentage, la pastille dit « ×N » plutôt que « +1 250 % ». */
export const SEUIL_FACTEUR = 900;

export interface DeltaProps {
  /** Variation signée, en pourcentage : `2.5` → « +2,5 % » ; ou `"nouveau"`. */
  valeur: ValeurDelta;
  /** Monter est mauvais (un coût, un délai). */
  inverse?: boolean;
  /** Le mot de `"nouveau"`. Défaut : « Nouveau » des libellés de la librairie. */
  libelleNouveau?: string;
  /** Après le pourcentage, dans l'infobulle. Défaut : « vs la période d'avant », même source. */
  libelleVs?: string;
  /** Pas d'infobulle (une cellule serrée) : le détail reste en `title`. */
  sansInfobulle?: boolean;
  className?: string;
}

export function Delta({ valeur, inverse = false, libelleNouveau, libelleVs, sansInfobulle = false, className }: DeltaProps) {
  const l = useLibelles().delta;
  let texte: string;
  let detail: string;
  let bon: boolean;
  if (valeur === "nouveau") {
    texte = libelleNouveau ?? l.nouveau;
    detail = l.rienAvant;
    bon = !inverse;
  } else {
    if (!Number.isFinite(valeur)) return null;
    const monte = valeur >= 0;
    bon = inverse ? !monte : monte;
    const abs = Math.abs(valeur);
    const pourcent = `${monte ? "+" : "−"}${formatPourcent(abs, abs < 10 && !Number.isInteger(valeur) ? 1 : 0)}`;
    if (abs >= SEUIL_FACTEUR) {
      const facteur = 1 + valeur / 100;
      texte = `×${formatNombre(facteur, Number.isInteger(facteur) ? 0 : 1)}`;
    } else {
      texte = pourcent;
    }
    detail = `${pourcent} ${libelleVs ?? l.vs}`;
  }
  const pastille = (
    <span
      className={cn("ui-stat-delta ui-stat-pilule", bon ? "ui-stat-delta-bon" : "ui-stat-delta-mauvais", className)}
      title={sansInfobulle ? detail : undefined}
    >
      {texte}
    </span>
  );
  return sansInfobulle ? pastille : <Infobulle texte={detail}>{pastille}</Infobulle>;
}
